import React, { useEffect, useRef, useState } from 'react';
import taskService, { PickableUser } from '../../services/taskService';
import '../../assets/styles/Workflow.css';

interface UserMultiPickerProps {
  value: number[];
  onChange: (userIds: number[]) => void;
  role?: string;
  placeholder?: string;
}

const UserMultiPicker: React.FC<UserMultiPickerProps> = ({ value, onChange, role, placeholder }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PickableUser[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Record<number, PickableUser>>({});
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    taskService.listPickableUsers(role, query).then((users) => {
      if (active) { setOptions(users.filter((u) => !value.includes(u.id))); setHighlighted(0); }
    }).catch(() => { if (active) setOptions([]); });
    return () => { active = false; };
  }, [query, role, open, value]);

  // Re-hydrate names for any selected IDs we don't have cached - e.g. after
  // this component remounts (step navigation unmounts/remounts it) and loses
  // its local selectedUsers cache while `value` (held by the parent) survives.
  useEffect(() => {
    const missingIds = value.filter((id) => !selectedUsers[id]);
    if (missingIds.length === 0) return;
    let active = true;
    taskService.getUsersByIds(missingIds).then((users) => {
      if (!active) return;
      setSelectedUsers((prev) => {
        const next = { ...prev };
        users.forEach((u) => { next[u.id] = u; });
        return next;
      });
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const add = (user: PickableUser) => {
    if (value.includes(user.id)) return;
    setSelectedUsers((prev) => ({ ...prev, [user.id]: user }));
    onChange([...value, user.id]);
    setQuery('');
  };

  const remove = (userId: number) => {
    onChange(value.filter((id) => id !== userId));
  };

  return (
    <div>
      {value.length > 0 && (
        <div className="wf-chip-row">
          {value.map((id) => (
            <span key={id} className="wf-chip">
              {selectedUsers[id]?.name || `User #${id}`}
              <button type="button" onClick={() => remove(id)}>&times;</button>
            </span>
          ))}
        </div>
      )}
      <div className="wf-picker" ref={containerRef}>
        <input
          type="text"
          className="wf-picker-input"
          placeholder={placeholder || 'Type a name to search...'}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (!open || options.length === 0) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => (i + 1) % options.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => (i - 1 + options.length) % options.length); }
            else if (e.key === 'Enter') { e.preventDefault(); add(options[highlighted]); }
            else if (e.key === 'Escape') { setOpen(false); }
          }}
        />
        {open && (
          <div className="wf-picker-dropdown">
            {options.length === 0 ? (
              <div className="wf-picker-empty">No matches</div>
            ) : (
              options.map((u, idx) => (
                <div
                  key={u.id}
                  className={`wf-picker-option${idx === highlighted ? ' active' : ''}`}
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => add(u)}
                >
                  <span>{u.name} <span style={{ color: '#9ca3af' }}>({u.email})</span></span>
                  <span className="wf-role-badge">{u.role_name}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserMultiPicker;
